import { Injectable } from '@angular/core';
import { UrlService } from '@uirouter/core';

import { WindowLocationService } from 'src/app/shared/services/location-helper.service';
import { UserStateService } from './user-state.service';

interface UrlStateObject {
  p?: string | null;
  u?: string | null;
  s?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UrlStateService {
  constructor(
    private windowLocationService: WindowLocationService,
    private urlService: UrlService,
    private userStateService: UserStateService,
  ) {}

  get(): string {
    let state = '';
    const stateObject: UrlStateObject = {
      p: null,
      u: null,
      s: null,
    };
    const location = this.windowLocationService.get();

    // Redirect to the URL root and append pathname back to the URL
    // on Authentication success
    // This prevents Domain authentication errors for sub-folders
    // Warning: Root folder must have CH available for this to work,
    // otherwise no redirect is performed!
    // Remove first character (/) from path since we're adding it to loc
    stateObject.p = location.pathname ? location.pathname.substring(1) : '';
    stateObject.s = location.search;
    stateObject.u = location.hash;

    if (stateObject.p || stateObject.s || stateObject.u) {
      state = encodeURIComponent(JSON.stringify(stateObject));
    }

    return state;
  }

  urlToState(param: string): string {
    if (!param) {
      return '';
    }

    let state = '';
    const stateObject: UrlStateObject = {
      p: null,
      u: null,
      s: null,
    };

    // Preserve legacy quirk: prefixing http:// before an absolute URL
    // yields host "http" and a path like "//localhost/...".
    const url = new URL('http://' + param);
    stateObject.p = url.host + url.pathname;
    stateObject.s = url.search;
    stateObject.u = url.hash;

    if (stateObject.p || stateObject.s || stateObject.u) {
      state = encodeURIComponent(JSON.stringify(stateObject));
    }

    return state;
  }

  redirectToState(stateString: string): void {
    const state = this.parseState(stateString);
    const location = this.windowLocationService.get();

    if (state.u || !this.urlService.config.html5Mode()) {
      // hash found, assume non HTML5 mode
      if (state.p || state.s) {
        // requires redirect
        this.userStateService._persistState();

        location.replace(state.p + state.s + state.u);
      } else {
        location.hash = state.u;
      }
    } else {
      // HTML5 mode. Navigate through UI-Router rather than calling
      // `history.pushState` directly. `pushState` only rewrites the address
      // bar and fires no event UI-Router listens to (it only subscribes to
      // `popstate` through Angular's LocationStrategy), so the router would
      // stay on the state it is currently showing - typically
      // `apps.auth.unauthorized` right after signing in - and the next param
      // sync would put its URL back. `UrlService.url()` sets the URL and
      // notifies the router, which then matches it to a state.
      const path = state.p || '/';

      this.urlService.url(
        (path.startsWith('/') ? path : '/' + path) + (state.s || ''),
        false,
      );
    }
  }

  clearStatePath(stateString: string): string {
    const state = this.parseState(stateString);

    state.p = undefined;
    state.s = undefined;

    return encodeURIComponent(JSON.stringify(state));
  }

  getUrlParam(paramName: string): string | null {
    // if page is reloaded, then you can get param from url search
    // otherwise you need to parse the path
    const search = this.urlService.search();

    if (search[paramName]) {
      return search[paramName] as string;
    }

    const decodedPath = decodeURIComponent(
      decodeURIComponent(
        decodeURIComponent(decodeURIComponent(this.urlService.path())),
      ),
    );
    return this.getParam(decodedPath, paramName);
  }

  getStateQueryParam(stateString: string, paramName: string): string | null {
    const state = this.parseState(stateString);
    return this.getParam(state.s, paramName);
  }

  private parseState(stateString: string): UrlStateObject {
    let state: UrlStateObject = {};

    try {
      state = JSON.parse(decodeURIComponent(stateString));
    } catch {
      // Parse failed
    }

    return state;
  }

  private getParam(
    paramString: string | null | undefined,
    paramName: string,
  ): string | null {
    const match = new RegExp(`[?&]${paramName}=([^&#"]*)`).exec(
      paramString || '',
    );
    return match && match[1];
  }
}
